import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SeedboxClean.css';
import Modal from '../../../../Components/Modal/Modal';
import Loader from '../../../../Components/Loader/Loader';
import AdminService from '../../../../services/Admin.service';
import convertBitToBytes from '../../../../utils/convertBitToBytes.utils';

// Steps: simulating -> preview -> confirm -> cleaning -> done (or error at any API step)
export default function SeedboxClean() {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState('idle');
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);
    const pendingRequest = useRef(null);

    const abortPending = () => {
        if (pendingRequest.current) {
            pendingRequest.current.abord();
            pendingRequest.current = null;
        }
    };

    const run = useCallback((dryRun) => {
        abortPending();
        setError(null);
        setStep(dryRun ? 'simulating' : 'cleaning');
        const http = AdminService.cleanSeedboxes(dryRun);
        pendingRequest.current = http;
        http.request
            .then((res) => {
                setReports(res.data || []);
                setStep(dryRun ? 'preview' : 'done');
            })
            .catch((err) => {
                if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) {
                    return;
                }
                const apiMessage = err && err.response && err.response.data && err.response.data.message;
                setError(apiMessage || (err && err.message) || 'Erreur inconnue');
                setStep(dryRun ? 'idle' : 'preview');
            })
            .finally(() => {
                if (pendingRequest.current === http) {
                    pendingRequest.current = null;
                }
            });
    }, []);

    const open = () => {
        setIsOpen(true);
        setReports([]);
        run(true);
    };

    const close = useCallback(() => {
        // Never abort a real cleanup midway: only the simulation can be cancelled
        if (step === 'cleaning') {
            return;
        }
        abortPending();
        setIsOpen(false);
        setStep('idle');
        setReports([]);
        setError(null);
    }, [step]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => abortPending, []);

    const removedCount = reports.reduce((acc, r) => acc + r.removed.length, 0);
    const removedSize = reports.reduce((acc, r) => acc + r.removed.reduce((s, t) => s + (t.size || 0), 0), 0);
    const errorCount = reports.reduce((acc, r) => acc + r.errors.length, 0);
    const isLoading = step === 'simulating' || step === 'cleaning';

    const summary = () => {
        if (step === 'done') {
            return `${removedCount} torrent(s) supprimé(s), ${convertBitToBytes(removedSize)} libéré(s).`;
        }
        return removedCount === 0
            ? 'Aucun torrent orphelin : les seedbox sont propres.'
            : `${removedCount} torrent(s) absent(s) de la base seront supprimé(s) des seedbox (${convertBitToBytes(removedSize)}).`;
    };

    const footer = () => {
        if (step === 'confirm') {
            return <>
                <span className="SeedboxClean-confirm-text">Supprimer définitivement {removedCount} torrent(s) et leurs fichiers ?</span>
                <button type="button" className="sm" onClick={() => setStep('preview')}>Annuler</button>
                <button type="button" className="sm danger" onClick={() => run(false)}>Confirmer la suppression</button>
            </>;
        }
        return <>
            {(step === 'preview' || step === 'done' || (step === 'idle' && error)) &&
                <button type="button" className="sm" onClick={() => run(true)}>Relancer l'analyse</button>}
            {step === 'preview' && removedCount > 0 &&
                <button type="button" className="sm danger" onClick={() => setStep('confirm')}>Supprimer {removedCount} torrent(s)</button>}
            <button type="button" className="sm" onClick={close} disabled={step === 'cleaning'}>Fermer</button>
        </>;
    };

    return <>
        <button type="button" className="sm warning" onClick={open}>Nettoyer les seedbox</button>
        <Modal
            isOpen={isOpen}
            title={step === 'done' ? 'Nettoyage des seedbox : terminé' : 'Nettoyage des seedbox : simulation'}
            onClose={close}
            footer={footer()}
        >
            <div className="SeedboxClean">
                {isLoading && <div className="SeedboxClean-loading">
                    <Loader isVisible={true}></Loader>
                    <p>{step === 'simulating' ? 'Analyse des seedbox en cours…' : 'Suppression en cours, ne fermez pas la page…'}</p>
                </div>}

                {error && <p className="error SeedboxClean-error">{error}</p>}

                {!isLoading && reports.length > 0 && <>
                    <p className={`SeedboxClean-summary ${step === 'done' ? 'SeedboxClean-summary--done' : ''}`}>
                        {summary()}
                        {errorCount > 0 && ` ${errorCount} erreur(s).`}
                    </p>
                    {reports.map((report) => <SeedboxReport key={report.seedbox} report={report} done={step === 'done'}></SeedboxReport>)}
                </>}
            </div>
        </Modal>
    </>;
}

function SeedboxReport({ report, done }) {
    const size = report.removed.reduce((s, t) => s + (t.size || 0), 0);
    return <section className="SeedboxClean-report">
        <h3>
            {report.seedbox}
            <span className="SeedboxClean-badge">{report.removed.length} {done ? 'supprimé(s)' : 'à supprimer'} · {convertBitToBytes(size)}</span>
            {report.errors.length > 0 && <span className="SeedboxClean-badge SeedboxClean-badge--danger">{report.errors.length} erreur(s)</span>}
        </h3>

        {report.removed.length > 0 && <div className="SeedboxClean-table">
            <table>
                <thead>
                    <tr>
                        <th className="SeedboxClean-name">Nom</th>
                        <th className="SeedboxClean-size">Taille</th>
                    </tr>
                </thead>
                <tbody>
                    {report.removed.map((torrent) => <tr key={torrent.hash} title={torrent.hash}>
                        <td className="SeedboxClean-name">{torrent.name || torrent.hash}</td>
                        <td className="SeedboxClean-size">{convertBitToBytes(torrent.size)}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>}

        {report.errors.length > 0 && <ul className="SeedboxClean-errors">
            {report.errors.map((err, index) => <li key={`${err.hash || 'global'}-${index}`}>
                {err.name && <strong>{err.name} : </strong>}{err.error}
            </li>)}
        </ul>}
    </section>;
}
